import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  Image as ImageIcon, 
  Clock, 
  XCircle,
  Download,
  Trash2,
  RefreshCw,
  Calendar,
  User
} from 'lucide-react'
import { hairstyleService, HairstyleGeneration } from '@/services/hairstyleService'
import { cn } from '@/lib/utils'

const HairstylePage: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  
  // 历史记录相关状态
  const [generationHistory, setGenerationHistory] = useState<HairstyleGeneration[]>([])
  const [selectedGeneration, setSelectedGeneration] = useState<HairstyleGeneration | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  
  // 图片查看相关状态
  const [selectedImage, setSelectedImage] = useState<{
    url: string
    title: string
    description?: string
  } | null>(null)

  // 加载初始数据
  useEffect(() => {
    loadGenerationHistory()
  }, [user])

  const loadGenerationHistory = async () => {
    if (!user) return
    
    try {
      setIsLoading(true)
      const history = await hairstyleService.getUserHairstyleGenerations(user.id, 50)
      setGenerationHistory(history)
    } catch (error) {
      console.error('Error loading generation history:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleViewGeneration = (generation: HairstyleGeneration) => {
    setSelectedGeneration(generation)
  }

  const handleImageClick = (url: string, title: string, description?: string) => {
    setSelectedImage({ url, title, description })
  }

  const handleDownloadImage = async (url: string, filename: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Error downloading image:', error)
      alert('下载失败，请重试')
    }
  }

  const handleDeleteGeneration = async (id: string) => {
    if (!confirm(t('hairstyle.deleteConfirm'))) return
    
    try {
      const result = await hairstyleService.deleteHairstyleGeneration(id)
      if (result.success) {
        // 刷新历史记录
        await loadGenerationHistory()
        if (selectedGeneration?.id === id) {
          setSelectedGeneration(null)
        }
      } else {
        alert(result.error || t('hairstyle.deleteFailed'))
      }
    } catch (error) {
      console.error('Error deleting generation:', error)
      alert(t('hairstyle.deleteFailed'))
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin" />
          <span className="ml-2">{t('hairstyle.loading')}</span>
        </div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{t('hairstyle.pleaseLogin')}</h2>
          <p className="text-gray-600">{t('hairstyle.loginDescription')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">{t('hairstyle.title')}</h1>
          <p className="text-lg text-muted-foreground">
            {t('hairstyle.subtitle')}
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* 左侧：历史记录列表 */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  {t('hairstyle.generationHistory')}
                </CardTitle>
                <CardDescription>
                  {t('hairstyle.totalRecords', { count: generationHistory.length })}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {generationHistory.length === 0 ? (
                  <div className="p-6 text-center">
                    <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">{t('hairstyle.noRecords')}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      {t('hairstyle.startFirst')}
                    </p>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    {generationHistory.map((generation) => (
                      <div
                        key={generation.id}
                        className={cn(
                          "p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors",
                          selectedGeneration?.id === generation.id && "bg-blue-50 border-blue-200"
                        )}
                        onClick={() => handleViewGeneration(generation)}
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={generation.original_image_url}
                            alt="原始图片"
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {formatDate(generation.created_at)}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={
                                generation.status === 'completed' ? 'default' :
                                generation.status === 'failed' ? 'destructive' :
                                generation.status === 'processing' ? 'secondary' : 'outline'
                              }>
                                {hairstyleService.getStatusText(generation.status)}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {generation.total_credit_cost} 积分
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* 右侧：详细信息 */}
          <div className="lg:col-span-2">
            {selectedGeneration ? (
              <div className="space-y-6">
                {/* 生成信息头部 */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Clock className="h-5 w-5" />
                          {t('hairstyle.generationDetails')}
                        </CardTitle>
                        <CardDescription>
                          {formatDate(selectedGeneration.created_at)} • {t('hairstyle.consumedCredits', { credits: selectedGeneration.total_credit_cost })}
                        </CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={
                          selectedGeneration.status === 'completed' ? 'default' :
                          selectedGeneration.status === 'failed' ? 'destructive' :
                          selectedGeneration.status === 'processing' ? 'secondary' : 'outline'
                        }>
                          {hairstyleService.getStatusText(selectedGeneration.status)}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteGeneration(selectedGeneration.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('hairstyle.generationTime')}:</span>
                        <span className="text-sm">{formatDate(selectedGeneration.created_at)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('hairstyle.status')}:</span>
                        <Badge variant={
                          selectedGeneration.status === 'completed' ? 'default' :
                          selectedGeneration.status === 'failed' ? 'destructive' :
                          selectedGeneration.status === 'processing' ? 'secondary' : 'outline'
                        }>
                          {hairstyleService.getStatusText(selectedGeneration.status)}
                        </Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">{t('hairstyle.consumedCreditsLabel')}:</span>
                        <span className="text-sm">{selectedGeneration.total_credit_cost}</span>
                      </div>
                      {selectedGeneration.completed_at && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">{t('hairstyle.completionTime')}:</span>
                          <span className="text-sm">{formatDate(selectedGeneration.completed_at)}</span>
                        </div>
                      )}
                      {selectedGeneration.error_message && (
                        <div>
                          <span className="text-sm text-gray-600">{t('hairstyle.errorMessage')}:</span>
                          <p className="text-sm text-red-600 mt-1">{selectedGeneration.error_message}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* 生成结果 */}
                <Card>
                  <CardHeader>
                    <CardTitle>{t('hairstyle.generationResults')}</CardTitle>
                    <CardDescription>
                      {selectedGeneration.config?.name || t('hairstyle.generationResults')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* 原始图片 */}
                      <div>
                        <h3 className="font-medium mb-3">{t('hairstyle.originalImage')}</h3>
                        <div className="relative group">
                          <img
                            src={selectedGeneration.original_image_url}
                            alt={t('hairstyle.originalImage')}
                            className="w-full h-64 object-cover rounded-lg cursor-pointer"
                            onClick={() => handleImageClick(
                              selectedGeneration.original_image_url,
                              t('hairstyle.originalImage'),
                              `${t('hairstyle.generationTime')}: ${formatDate(selectedGeneration.created_at)}`
                            )}
                          />
                          {/* <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                            <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div> */}
                        </div>
                      </div>

                      {/* 生成结果图片 */}
                      <div>
                        <h3 className="font-medium mb-3">{t('hairstyle.generatedResult')}</h3>
                        <div className="relative group">
                          {selectedGeneration.generated_image_url ? (
                            <>
                              <img
                                src={selectedGeneration.generated_image_url}
                                alt={selectedGeneration.config?.name || t('hairstyle.generatedResult')}
                                className="w-full h-64 object-cover rounded-lg cursor-pointer"
                                onClick={() => handleImageClick(
                                  selectedGeneration.generated_image_url!,
                                  selectedGeneration.config?.name || t('hairstyle.generatedResult'),
                                  selectedGeneration.config?.hairstyle_description
                                )}
                              />
                              {/* <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center">
                                <ZoomIn className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div> */}
                              <div className="absolute top-2 right-2">
                                <Badge variant={
                                  selectedGeneration.status === 'completed' ? 'default' :
                                  selectedGeneration.status === 'failed' ? 'destructive' :
                                  selectedGeneration.status === 'processing' ? 'secondary' : 'outline'
                                }>
                                  {hairstyleService.getStatusText(selectedGeneration.status)}
                                </Badge>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-64 bg-gray-900 rounded-lg flex items-center justify-center">
                              {selectedGeneration.status === 'processing' ? (
                                <div className="text-center">
                                  <RefreshCw className="h-12 w-12 animate-spin text-gray-400 mx-auto mb-2" />
                                  <p className="text-gray-400">{t('hairstyle.processing')}</p>
                                </div>
                              ) : selectedGeneration.status === 'failed' ? (
                                <div className="text-center">
                                  <XCircle className="h-12 w-12 text-red-400 mx-auto mb-2" />
                                  <p className="text-red-400">{t('hairstyle.failed')}</p>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                                  <p className="text-gray-400">{t('hairstyle.waiting')}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* 发型信息 */}
                        {selectedGeneration.config && (
                          <div className="mt-3">
                            <h4 className="font-medium text-sm">
                              {selectedGeneration.config.name}
                            </h4>
                            <p className="text-xs text-gray-600 mt-1">
                              {selectedGeneration.config.hairstyle_description} - {selectedGeneration.config.hair_color}
                            </p>
                            
                            {selectedGeneration.status === 'completed' && selectedGeneration.generated_image_url && (
                              <Button 
                                size="sm" 
                                className="w-full mt-2"
                                onClick={() => handleDownloadImage(
                                  selectedGeneration.generated_image_url!,
                                  `${selectedGeneration.config?.name || t('hairstyle.generatedResult')}-${Date.now()}.png`
                                )}
                              >
                                <Download className="h-4 w-4 mr-2" />
                                {t('hairstyle.download')}
                              </Button>
                            )}
                            
                            {selectedGeneration.status === 'failed' && selectedGeneration.error_message && (
                              <p className="text-xs text-red-500 mt-2">
                                {selectedGeneration.error_message}
                              </p>
                            )}
                            
                            {selectedGeneration.processing_time_seconds && (
                              <p className="text-xs text-gray-500 mt-1">
                                {t('hairstyle.processingTime')}: {hairstyleService.formatProcessingTime(selectedGeneration.processing_time_seconds)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <ImageIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">{t('hairstyle.selectRecord')}</h3>
                  <p className="text-gray-600">{t('hairstyle.selectRecordDescription')}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* 图片查看模态框 */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl max-h-full overflow-auto">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-medium">{selectedImage.title}</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedImage(null)}
                >
                  ✕
                </Button>
              </div>
              <div className="p-4">
                <img
                  src={selectedImage.url}
                  alt={selectedImage.title}
                  className="w-full h-auto max-h-96 object-contain"
                />
                {selectedImage.description && (
                  <p className="text-sm text-gray-600 mt-4">{selectedImage.description}</p>
                )}
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => handleDownloadImage(
                      selectedImage.url,
                      `${selectedImage.title}-${Date.now()}.png`
                    )}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {t('hairstyle.download')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setSelectedImage(null)}
                  >
                    {t('common.close')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default HairstylePage
